import { ExtractRouteParams } from 'react-router'
import { generatePath } from 'react-router-dom'

import { ArtistAppLink, Maybe } from '@/clients/utoniq-core/schema'
import { config } from '@/config'

/**
 * @description
 * URLのクエリパラメータのうち、{key}の文字をkeyMapで指定した値に置換する
 * e.g.
 * {
 *  url: https://example.com?name={name},
 *  keyMap: { name: "alice" }
 * }
 * ↓
 * https://example.com?name=alice
 */

export const replaceParameters = (
  url: string,
  keyMap: {
    [key: string]: string
  }
) => {
  let replacedUrl = url
  Object.keys(keyMap).forEach(key => {
    replacedUrl = replacedUrl.replace(`{${key}}`, keyMap[key])
  })

  return replacedUrl
}

/**
 * @description generate domain + path
 * @param path
 * @param options
 * @example
 * path: /test/:testId
 * option: testId: 100
 * ↓
 * https://example.com/test/100
 */
export const generateFullPath = <S extends string>(
  path: S,
  options: ExtractRouteParams<S>
): string => {
  return `${config.domain}${generatePath(path, options)}`
}

export const encodeUrl = (url?: string | null) => {
  if (!url) return url
  const isEncoded = decodeURI(url) !== url
  if (!isEncoded) return encodeURI(url)
  return url
}

/**
 * Determines whether a given URL is an external link.
 * An external link is defined as a link with a protocol of `http` or `https`
 * @param {string} url - The URL to check. Can be absolute (e.g., "https://example.com") or relative (e.g., "/about").
 */
export const isExternalLink = (url: string): boolean => {
  // Define a regular expression to match URLs with `http` or `https` protocol
  const externalLinkRegex = /^https?:\/\/([^/]+)/i

  // Match the given URL against the regex to extract the protocol and host
  return !!url.match(externalLinkRegex)
}

type AppLinkType = 'bottomTab:profile'

/**
 * Get artist app link by type
 * @param appLinks
 * @param type
 */
export const getArtistAppLink = <T extends Maybe<ArtistAppLink>>(
  appLinks: Maybe<T[]> | undefined,
  type: AppLinkType
): T | undefined | null => {
  return appLinks?.find(appLink => appLink?.type === type)
}
